<template>
    <div class="wrap">
        <div class="d-flex">
            <div class="mr-auto">
                <button class="btn btn-secondary" @click="refresh"><i class="fas fa-sync-alt"></i></button>
                <slot name="buttons"></slot>
            </div>
            <div class="align-self-end">
                <input-debounced
                        v-model="search"
                        :clearable="true"
                        :autofocus="true"
                        placeholder="Search"
                        class="search-bar w-300 mb-2"
                ></input-debounced>
            </div>
        </div>
        <base-table
                :base-url="urlWithFilters"
                ref="baseTable"
                @load-success="onLoadSuccess"
                @refresh="onRefresh"
                :current-page="currentPage"
                v-loading="isRefreshing"
                element-loading-spinner="el-icon-loading"
        >
            <template v-slot>
                <el-table
                        :data="items"
                        stripe
                        class="w-100"
                >
                    <el-table-column type="expand" v-slot="{row}">
                        <expand-column :row="row"></expand-column>
                    </el-table-column>
                    <el-table-column
                            prop="model.id"
                            label="Id"
                            min-width="60"
                    ></el-table-column>
                    <el-table-column
                            prop="model.body"
                            label="Content"
                            min-width="300"
                            v-slot="{row}"
                    >
                        <div class="message-input">{{ formatContent(row.model.body) }}</div>
                    </el-table-column>
                    <el-table-column
                            prop="model.status_text"
                            label="Status"
                            min-width="80"
                            v-slot="{row}"
                    >
                        <status-column :row="row"></status-column>
                    </el-table-column>
                    <el-table-column
                            prop="model.outgoing_number"
                            label="Sender"
                            :formatter="formatOutgoingNumber"
                            min-width="300"
                    ></el-table-column>
                    <el-table-column
                            prop="model.contact_list.name"
                            label="Contact List"
                            min-width="300"
                    ></el-table-column>
                    <el-table-column
                            prop="model.start_at"
                            label="Start"
                            min-width="120"
                            :formatter="formatDate"
                    ></el-table-column>
                    <el-table-column
                            prop="model.progress"
                            label="Progress"
                            min-width="100"
                            v-slot="{row}"

                    >
                        <progress-column :row="row" :statuses="statuses"></progress-column>
                    </el-table-column>
                    <el-table-column
                            label="Actions"
                            v-slot="{row}"
                            min-width="132"
                    >
                        <actions-column :row="row" @edit="onEdit" @delete="onDelete" @clone="onClone" @view="onView" @clone-with-contacts="onCloneWithContacts"></actions-column>
                    </el-table-column>
                </el-table>
            </template>
        </base-table>
        <el-pagination
                layout="prev, pager, next"
                :total="total"
                :current-page.sync="currentPage"
                :page-size="pageSize"
                class="text-right"
        ></el-pagination>
    </div>
</template>

<script>
  import TableWithPagination from '../table-with-pagination';
  import {addQueryParams} from '../../libs/url';
  import ActionsColumn from './actions-column';
  import ExpandColumn from './campaign-expand-column';
  import StatusColumn from './status-column';
  import ProgressColumn from './progress';
  import {strLimit} from '../../helpers';

  export default {
    extends: TableWithPagination,

    components: {
      ActionsColumn,
      ExpandColumn,
      StatusColumn,
      ProgressColumn,
    },

    data() {
      return {
        search: null,
        filters: {},
      };
    },

    computed: {
      /**
       * @return {string}
       */
      urlWithFilters() {
        return addQueryParams(this.url, this.filters);
      },

      /**
       * @return {object}
       */
      statuses() {
        return config('constants.campaign.statuses', {});
      },
    },

    watch: {
      search: function (value) {
        if (value) {
          this.filters = _.assign({}, this.filters, {search: value});
        } else {
          this.filters = _.omit(this.filters, ['search']);
        }
      },
    },

    methods: {
      /**
       * @param {object} row
       * @param {object} col
       * @param {object|null} value
       */
      formatDate(row, col, value) {
        const datetime = _.get(value, 'datetime', null);

        return _.isNull(datetime)
          ? '-'
          : moment(datetime).format(config('format.moment.short.datetime'));
      },

      /**
       * @param {object} row
       * @param {object} col
       * @param {string} presentation
       * @return {string}
       */
      formatOutgoingNumber(row, col, {presentation}) {
        return presentation;
      },

      /**
       * @param {string} body
       * @return {string}
       */
      formatContent(body) {
        const cleared = String(body).replace(/\s+/g, ' ');

        return strLimit(cleared, 80);
      },

      /**
       * @return {undefined}
       */
      refresh() {
        this.$refs.baseTable.refresh();
      },

      /**
       * @param {string} url
       * @event show-add-campaign-dialog
       */
      onEdit({url}) {
        eventBus.$emit('show-add-campaign-dialog', url);
      },

      /**
       * @param {string} url
       */
      onClone({url}) {
        axios.post(url, {_method: 'POST'})
          .then(({data: {message}}) => {
            _.isString(message) && this.$message.success(message);
            this.refresh();
          })
          .catch((err) => {
            const message = _.get(err, 'response.data.message');
            _.isString(message) && this.$message.error(message);
          });
      },

      /**
       * @param {string} url
       */
      onCloneWithContacts({url}) {
        axios.post(url, {_method: 'POST'})
          .then(({data: {message}}) => {
            _.isString(message) && this.$message.success(message);
            this.refresh();
          })
          .catch((err) => {
            const message = _.get(err, 'response.data.message');
            _.isString(message) && this.$message.error(message);
          });
      },

      /**
       * @param {string} url
       */
      onDelete({url}) {
        axios.post(url, {_method: 'DELETE'})
          .then(({data: {message}}) => {
            _.isString(message) && this.$message.success(message);
            this.refresh();
          })
          .catch((err) => {
            const message = _.get(err, 'response.data.message');
            _.isString(message) && this.$message.error(message);
          });
      },

      /**
       * @param {string} url
       * @event show-view-campaign-dialog
       */
      onView({url}) {
        eventBus.$emit('show-view-campaign-dialog', url);
      },
    }
  }
</script>
